import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, Calendar, Briefcase, FileText, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import SystemAlertsWidget from '@/components/dashboard/SystemAlertsWidget';

// ✅ API Configuration
const API_BASE = 'https://new.sbdemolition.online/api';

const dashboardAPI = {
  getStats: async () => {
    const res = await fetch(`${API_BASE}/dashboard/stats.php`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch stats');
    return res.json();
  },
  
  checkClockInStatus: async (userId) => {
    const res = await fetch(`${API_BASE}/attendance/check-status.php?userId=${encodeURIComponent(userId)}`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to check attendance');
    return res.json();
  }
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeProjects: 0,
    pendingTasks: 0,
    recentActivity: [],
    unreadNotifications: 0
  });
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        setLoading(true);
        
        // ✅ Load dashboard stats
        const statsRes = await dashboardAPI.getStats();
        if (statsRes.success) {
          setStats({
            activeProjects: statsRes.data.activeProjects || 0,
            pendingTasks: statsRes.data.pendingTasks || 0,
            recentActivity: statsRes.data.recentActivity || [],
            unreadNotifications: statsRes.data.unreadNotifications || 0
          });
        }
        
        // ✅ Check clock-in status
        const clockRes = await dashboardAPI.checkClockInStatus(user.id);
        if (clockRes.success) {
          setIsClockedIn(clockRes.isClockedIn);
        }
      } catch (e) {
        console.error("Dashboard data load error:", e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  if (!user) return null;

  const firstName = user.name ? user.name.split(' ')[0] : 'User';

  return (
    <>
      <Helmet><title>Dashboard - Stone Beam</title></Helmet>

      <div className="space-y-6 animate-in fade-in duration-500">
        {/* ✅ Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Welcome back, {firstName}!</h1>
            <p className="text-slate-600">Here's what's happening with your projects today.</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => navigate('/hr/attendance')} 
              className={`${isClockedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {isClockedIn ? 'Clock Out' : 'Clock In Now'}
            </Button>
          </div>
        </div>

        {/* ✅ Top Widgets - 4 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Active Projects */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/projects')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full text-blue-600">
                <Briefcase className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Active Projects</p>
                <h3 className="text-2xl font-bold">{loading ? '...' : stats.activeProjects}</h3>
              </div>
            </CardContent>
          </Card>
          
          {/* Pending Tasks */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-full text-orange-600">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Pending Tasks</p>
                <h3 className="text-2xl font-bold">{loading ? '...' : stats.pendingTasks}</h3>
              </div>
            </CardContent>
          </Card>
          
          {/* Attendance Status */}
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/hr/attendance')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`p-3 rounded-full ${isClockedIn ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Attendance Status</p>
                <h3 className={`text-lg font-bold ${isClockedIn ? 'text-green-600' : 'text-slate-600'}`}>
                  {loading ? '...' : (isClockedIn ? 'Clocked In' : 'Not Active')}
                </h3>
              </div>
            </CardContent>
          </Card>
          
          {/* Notifications */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-full text-yellow-600">
                <Bell className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Notifications</p>
                <h3 className="text-2xl font-bold">{loading ? '...' : stats.unreadNotifications}</h3>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ✅ Bottom Section - 2 Columns */}
        <div className="grid md:grid-cols-2 gap-6 items-start">
          {/* Recent Activity Card */}
          <Card className="h-full border-stone-200/60 shadow-md">
            <CardHeader className="border-b border-stone-100 bg-slate-50/50">
              <CardTitle className="flex items-center gap-2 text-navy-steal">
                <Calendar className="w-5 h-5 text-safety-orange" /> Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 max-h-[500px] overflow-y-auto">
              {loading ? (
                <p className="text-sm text-stone-500 italic">Loading activities...</p>
              ) : stats.recentActivity.length > 0 ? (
                <div className="space-y-6 pl-4 border-l border-slate-200">
                  {stats.recentActivity.map((activity, idx) => (
                    <div key={idx} className="relative group">
                      <div className="absolute -left-[21px] w-3 h-3 bg-white rounded-full border-2 border-stone-300 group-hover:border-safety-orange transition-colors" />
                      <p className="text-xs text-stone-400 font-mono mb-0.5">
                        {new Date(activity.date).toLocaleString()}
                      </p>
                      <p className="text-sm font-semibold text-navy-steal">{activity.title}</p>
                      <p className="text-xs text-stone-500">{activity.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-500 italic">No recent activity found.</p>
              )}
            </CardContent>
          </Card>

          {/* System Alerts Widget */}
          <SystemAlertsWidget />
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
